import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Trash2, Plus, LogOut, Pencil } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import { Field, TextInput, TextArea, SubmitButton } from "@/components/forms";

const TABS = ["Submissions", "Settings", "Sermons", "Events"];
const SUB_TYPES = [
  ["", "All"], ["service1", "Service #1"], ["plan_visit", "Plan Your Visit"],
  ["prayer", "Prayer"], ["contact", "Contact"], ["launch_text", "Launch Text"],
];

const SETTINGS_FIELDS = [
  ["launch_datetime", "Launch date & time (Service #1)", "datetime-local"],
  ["church_name", "Church name", "text"],
  ["sunday_time", "Sunday service time", "text"],
  ["wednesday_time", "Wednesday service time", "text"],
  ["service_length_text", "Expected service length (shown in What To Expect)", "text"],
  ["parking_info_text", "Parking instructions (shown in What To Expect & Location)", "textarea"],
  ["address_street", "Street address", "text"],
  ["address_city", "City", "text"],
  ["address_state", "State", "text"],
  ["address_zip", "ZIP", "text"],
  ["phone", "Church phone", "text"],
  ["email", "Church email", "text"],
  ["announcement_text", "Announcement bar text", "text"],
  ["announcement_cta_label", "Announcement button label", "text"],
  ["logo_url", "Official VC Clay logo URL (leave blank to use the built-in logo)", "text"],
  ["giving_url", "Giving platform URL", "text"],
  ["recurring_giving_url", "Recurring giving URL", "text"],
  ["facebook", "Facebook URL", "text"],
  ["instagram", "Instagram URL", "text"],
  ["youtube", "YouTube URL", "text"],
  ["pastor_name", "Pastor name", "text"],
  ["pastor_title", "Pastor title", "text"],
  ["pastor_welcome", "Pastor welcome message", "textarea"],
  ["pastor_bio", "Pastor biography (full - About page)", "textarea"],
  ["pastor_bio_short", "Pastor biography (short - homepage, 3-4 sentences)", "textarea"],
  ["pastor_photo_url", "Pastor photo URL", "text"],
  ["pastor_family_photo_url", "Pastor family photo URL", "text"],
  ["beliefs_text", "What We Believe text", "textarea"],
  ["fellowship_text", "Our Fellowship text", "textarea"],
  ["vision_text", "Vision statement (local church, worldwide vision)", "textarea"],
];

const EMPTY_SERMON = { title: "", speaker: "", date: "", scripture: "", series: "", description: "", youtube_id: "", thumbnail: "" };
const EMPTY_EVENT = { title: "", date: "", time: "", location: "", description: "", image: "", registration_url: "" };

function Toggle({ label, checked, onChange, testId }) {
  return (
    <label className="flex cursor-pointer items-center justify-between rounded-xl border border-slate-200 bg-white px-4 py-3">
      <span className="text-sm font-semibold text-navy">{label}</span>
      <button type="button" role="switch" aria-checked={checked} data-testid={testId} onClick={() => onChange(!checked)}
        className={`relative h-7 w-12 rounded-full transition-colors ${checked ? "bg-victory" : "bg-slate-300"}`}>
        <span className={`absolute top-1 h-5 w-5 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </button>
    </label>
  );
}

function SubmissionsTab() {
  const [subs, setSubs] = useState([]);
  const [filter, setFilter] = useState("");
  const load = useCallback(() => {
    api.get(`/admin/submissions${filter ? `?type=${filter}` : ""}`).then(({ data }) => setSubs(data)).catch((e) => toast.error(formatApiError(e)));
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-submissions-tab">
      <div className="mb-6 flex flex-wrap gap-2">
        {SUB_TYPES.map(([v, label]) => (
          <button key={v} onClick={() => setFilter(v)} data-testid={`sub-filter-${v || "all"}`}
            className={`rounded-full px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${filter === v ? "bg-navy text-white" : "border border-navy/15 bg-white text-navy hover:border-navy"}`}>
            {label}
          </button>
        ))}
      </div>
      {subs.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-navy/20 bg-white p-10 text-center text-slate-500">No submissions yet.</p>
      ) : (
        <div className="space-y-4">
          {subs.map((s) => (
            <div key={s.id} className="rounded-2xl border border-navy/10 bg-white p-5" data-testid={`submission-${s.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="rounded-full bg-navy/5 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-navy">{s.type.replace("_", " ")}</span>
                  <p className="mt-2 text-xs text-slate-400">{new Date(s.created_at).toLocaleString()}</p>
                </div>
                <button onClick={async () => { await api.delete(`/admin/submissions/${s.id}`); load(); }} data-testid={`submission-delete-${s.id}`} aria-label="Delete submission"
                  className="rounded-full p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-victory"><Trash2 size={16} /></button>
              </div>
              <dl className="mt-3 grid gap-x-8 gap-y-1.5 text-sm sm:grid-cols-2">
                {Object.entries(s.data).map(([k, v]) => (
                  <div key={k} className="flex gap-2">
                    <dt className="font-semibold capitalize text-navy">{k.replace(/_/g, " ")}:</dt>
                    <dd className="text-slate-600">{String(v)}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SettingsTab() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/settings").then(({ data }) => setForm(data)); }, []);

  if (!form) return <p className="text-slate-500">Loading settings...</p>;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      if (payload.launch_datetime && payload.launch_datetime.length === 16) payload.launch_datetime += ":00-05:00";
      await api.put("/admin/settings", payload);
      toast.success("Settings saved - live site updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-3xl space-y-5" data-testid="admin-settings-form">
      <Toggle label="Pre-launch mode (countdown hero + Service #1 registration)" checked={!!form.launch_mode} onChange={(v) => set("launch_mode", v)} testId="settings-launch-mode-toggle" />
      <Toggle label="Announcement bar enabled" checked={!!form.announcement_enabled} onChange={(v) => set("announcement_enabled", v)} testId="settings-announcement-toggle" />
      <div className="grid gap-5 sm:grid-cols-2">
        {SETTINGS_FIELDS.map(([key, label, type]) => (
          <div key={key} className={type === "textarea" || key === "address" ? "sm:col-span-2" : ""}>
            <Field label={label} id={`set-${key}`}>
              {type === "textarea" ? (
                <TextArea id={`set-${key}`} value={form[key] || ""} onChange={(e) => set(key, e.target.value)} />
              ) : (
                <TextInput id={`set-${key}`} type={type} value={(form[key] || "").slice(0, type === "datetime-local" ? 16 : undefined)} onChange={(e) => set(key, e.target.value)} />
              )}
            </Field>
          </div>
        ))}
      </div>
      <SubmitButton testId="settings-save-button" loading={saving}>SAVE SETTINGS</SubmitButton>
    </form>
  );
}

function SermonsTab() {
  const [sermons, setSermons] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/sermons").then(({ data }) => setSermons(data)), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await api.put(`/admin/sermons/${editing.id}`, editing);
      else await api.post("/admin/sermons", editing);
      toast.success("Sermon saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-sermons-tab">
      {editing ? (
        <form onSubmit={save} className="max-w-2xl space-y-5 rounded-2xl border border-navy/10 bg-white p-6" data-testid="admin-sermon-form">
          <h3 className="font-display text-lg font-800 text-navy">{editing.id ? "Edit Sermon" : "New Sermon"}</h3>
          {[["title", "Title"], ["speaker", "Speaker"], ["date", "Date (YYYY-MM-DD)"], ["scripture", "Scripture"], ["series", "Series"], ["youtube_id", "YouTube video ID"], ["thumbnail", "Thumbnail URL"]].map(([k, label]) => (
            <Field key={k} label={label} id={`sm-${k}`}>
              <TextInput id={`sm-${k}`} value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} required={k === "title"} />
            </Field>
          ))}
          <Field label="Description" id="sm-description">
            <TextArea id="sm-description" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
          </Field>
          <div className="flex gap-3">
            <SubmitButton testId="sermon-save-button">SAVE SERMON</SubmitButton>
            <button type="button" onClick={() => setEditing(null)} data-testid="sermon-cancel-button" className="rounded-full border border-navy/20 px-6 py-3 text-sm font-bold text-navy">Cancel</button>
          </div>
        </form>
      ) : (
        <>
          <button onClick={() => setEditing({ ...EMPTY_SERMON })} data-testid="sermon-add-button"
            className="mb-6 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 font-display text-sm font-700 text-white transition-transform hover:-translate-y-0.5">
            <Plus size={16} /> ADD SERMON
          </button>
          <div className="space-y-3">
            {sermons.map((s) => (
              <div key={s.id} className="flex items-center justify-between gap-4 rounded-2xl border border-navy/10 bg-white p-5" data-testid={`admin-sermon-${s.id}`}>
                <div>
                  <p className="font-display font-700 text-navy">{s.title} {s.sample && <span className="ml-2 rounded-full bg-victory/10 px-2 py-0.5 text-[10px] font-bold uppercase text-victory">Sample</span>}</p>
                  <p className="text-xs text-slate-500">{s.speaker} - {s.date} - /sermons/{s.slug}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(s)} data-testid={`sermon-edit-${s.id}`} aria-label={`Edit ${s.title}`} className="rounded-full p-2 text-slate-400 hover:bg-navy/5 hover:text-navy"><Pencil size={16} /></button>
                  <button onClick={async () => { await api.delete(`/admin/sermons/${s.id}`); load(); }} data-testid={`sermon-delete-${s.id}`} aria-label={`Delete ${s.title}`} className="rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-victory"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function EventsTab() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get("/events").then(({ data }) => setEvents([...data.upcoming, ...data.past])), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.id) await api.put(`/admin/events/${editing.id}`, editing);
      else await api.post("/admin/events", editing);
      toast.success("Event saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-events-tab">
      {editing ? (
        <form onSubmit={save} className="max-w-2xl space-y-5 rounded-2xl border border-navy/10 bg-white p-6" data-testid="admin-event-form">
          <h3 className="font-display text-lg font-800 text-navy">{editing.id ? "Edit Event" : "New Event"}</h3>
          {[["title", "Title"], ["date", "Date (YYYY-MM-DD)"], ["time", "Time"], ["location", "Location"], ["image", "Image URL"], ["registration_url", "Registration URL"]].map(([k, label]) => (
            <Field key={k} label={label} id={`ev-${k}`}>
              <TextInput id={`ev-${k}`} value={editing[k] || ""} onChange={(e) => setEditing({ ...editing, [k]: e.target.value })} required={["title", "date"].includes(k)} />
            </Field>
          ))}
          <Field label="Description" id="ev-description">
            <TextArea id="ev-description" value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
          </Field>
          <div className="flex gap-3">
            <SubmitButton testId="event-save-button">SAVE EVENT</SubmitButton>
            <button type="button" onClick={() => setEditing(null)} data-testid="event-cancel-button" className="rounded-full border border-navy/20 px-6 py-3 text-sm font-bold text-navy">Cancel</button>
          </div>
        </form>
      ) : (
        <>
          <button onClick={() => setEditing({ ...EMPTY_EVENT })} data-testid="event-add-button"
            className="mb-6 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 font-display text-sm font-700 text-white transition-transform hover:-translate-y-0.5">
            <Plus size={16} /> ADD EVENT
          </button>
          <div className="space-y-3">
            {events.map((ev) => (
              <div key={ev.id} className="flex items-center justify-between gap-4 rounded-2xl border border-navy/10 bg-white p-5" data-testid={`admin-event-${ev.id}`}>
                <div>
                  <p className="font-display font-700 text-navy">{ev.title} {ev.sample && <span className="ml-2 rounded-full bg-victory/10 px-2 py-0.5 text-[10px] font-bold uppercase text-victory">Sample</span>}</p>
                  <p className="text-xs text-slate-500">{ev.date} {ev.time && `- ${ev.time}`} {ev.location && `- ${ev.location}`}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditing(ev)} data-testid={`event-edit-${ev.id}`} aria-label={`Edit ${ev.title}`} className="rounded-full p-2 text-slate-400 hover:bg-navy/5 hover:text-navy"><Pencil size={16} /></button>
                  <button onClick={async () => { await api.delete(`/admin/events/${ev.id}`); load(); }} data-testid={`event-delete-${ev.id}`} aria-label={`Delete ${ev.title}`} className="rounded-full p-2 text-slate-400 hover:bg-red-50 hover:text-victory"><Trash2 size={16} /></button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminDashboard() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("Submissions");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/me")
      .then(({ data }) => setUser(data))
      .catch(() => { localStorage.removeItem("vc_admin_token"); navigate("/admin/login"); })
      .finally(() => setChecking(false));
  }, [navigate]);

  if (checking) return <div className="flex min-h-screen items-center justify-center bg-cream text-slate-500">Loading...</div>;
  if (!user) return null;

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-navy/10 bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-slate-500 sm:block">{user.email}</span>
            <button onClick={() => { localStorage.removeItem("vc_admin_token"); navigate("/admin/login"); }} data-testid="admin-logout-button"
              className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-4 py-2 text-xs font-bold text-navy transition-colors hover:bg-navy hover:text-white">
              <LogOut size={14} /> SIGN OUT
            </button>
          </div>
        </div>
        <nav className="mx-auto flex max-w-7xl gap-2 overflow-x-auto px-5 pb-3 sm:px-8" aria-label="Admin sections">
          {TABS.map((t) => (
            <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t.toLowerCase()}`}
              className={`whitespace-nowrap rounded-full px-5 py-2.5 font-display text-sm font-700 transition-colors ${tab === t ? "bg-navy text-white" : "text-navy hover:bg-navy/5"}`}>
              {t}
            </button>
          ))}
        </nav>
      </header>
      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        {tab === "Submissions" && <SubmissionsTab />}
        {tab === "Settings" && <SettingsTab />}
        {tab === "Sermons" && <SermonsTab />}
        {tab === "Events" && <EventsTab />}
      </main>
    </div>
  );
}
